/**
 * UpdateVersionContext
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE lightweight onSnapshot listener lives here for the entire app.
 * It watches a single per-tenant document:
 *   wholesalers/{wholesalerId}/updateVersions
 *
 * ISSUE-3 FIX: path moved from the global appConfig/updateVersions document
 * to a tenant-scoped document. The old path was a single shared counter for
 * all tenants — bumping it for Tenant A caused Tenant B's retailer clients
 * to trigger spurious refetches. The new path is isolated per wholesaler, so
 * a product or order change in one tenant never signals another tenant's clients.
 *
 * Structure:
 *   { ordersVersion: number, returnsVersion: number, productsVersion: number }
 *
 * How it works:
 *   • Admin writes increment ordersVersion / returnsVersion / productsVersion
 *     whenever they update an order, return, or product.
 *   • Retailer pages (Orders, Returns, RewardsTracker) subscribe to this context.
 *     When the version they care about changes, they show a banner:
 *     "New updates are available. Tap Refresh to see latest changes."
 *   • Data is NOT auto-reloaded — the user must click Refresh manually.
 *   • This replaces 3–4 heavy per-user onSnapshot listeners with a single
 *     1-field document read shared by all retailer sessions in one tenant.
 *   • The listener is a no-op until wholesalerId is known (user not yet
 *     authenticated or wholesaler not yet resolved). Once set, it subscribes
 *     and stays subscribed for the lifetime of the session.
 */

import { createContext, useContext, useEffect, useRef, useState } from "react";
import { db } from "../services/firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { useAuth } from "./AuthContext";

export const UpdateVersionContext = createContext(null);

export function UpdateVersionProvider({ children }) {
  const { wholesalerId } = useAuth();

  const [versions, setVersions] = useState({
    ordersVersion:   0,
    returnsVersion:  0,
    productsVersion: 0,
  });

  useEffect(() => {
    // Wait until the tenant identity is resolved before subscribing.
    // Without this guard, the listener would target an invalid doc path and
    // fire permission-denied errors for unauthenticated / pre-login renders.
    if (!wholesalerId) return;

    const unsub = onSnapshot(
      doc(db, "wholesalers", wholesalerId, "updateVersions", "current"),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setVersions({
            ordersVersion:   data.ordersVersion  ?? 0,
            returnsVersion:  data.returnsVersion  ?? 0,
            productsVersion: data.productsVersion ?? 0,
          });
        }
      },
      (err) => {
        // Non-fatal: retailers can still use the app without the banner feature
        console.warn("UpdateVersionContext: could not listen to version doc:", err.message);
      }
    );
    return () => unsub();
  }, [wholesalerId]);

  return (
    <UpdateVersionContext.Provider value={versions}>
      {children}
    </UpdateVersionContext.Provider>
  );
}

export function useUpdateVersions() {
  const ctx = useContext(UpdateVersionContext);
  if (!ctx) throw new Error("useUpdateVersions must be used within UpdateVersionProvider");
  return ctx; // { ordersVersion, returnsVersion, productsVersion }
}

/**
 * useVersionBanner
 * ─────────────────────────────────────────────────────────────────────────────
 * Drop-in hook for retailer pages. Tracks when the version for a specific
 * collection has changed since the last fetch, and returns:
 *
 *   { showBanner, dismissBanner, resetBaseVersion }
 *
 * Usage:
 *   const { showBanner, dismissBanner, resetBaseVersion } =
 *     useVersionBanner("ordersVersion");
 *
 *   // After getDocs() completes, call resetBaseVersion() so the banner
 *   // only shows if a NEW change arrives after this fetch.
 */
export function useVersionBanner(versionKey) {
  const versions = useUpdateVersions();
  const currentVersion = versions[versionKey] ?? 0;

  // Store the version at which data was last fetched
  const baseRef = useRef(null);
  const [showBanner, setShowBanner] = useState(false);

  // On first render, set base to current version (no stale banner on mount)
  useEffect(() => {
    if (baseRef.current === null) {
      baseRef.current = currentVersion;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Whenever version increments beyond the base, show the banner
  useEffect(() => {
    if (baseRef.current !== null && currentVersion > baseRef.current) {
      setShowBanner(true);
    }
  }, [currentVersion]);

  // Call after getDocs() succeeds — syncs base to latest version
  const resetBaseVersion = () => {
    baseRef.current = currentVersion;
    setShowBanner(false);
  };

  // User explicitly dismisses without refreshing
  const dismissBanner = () => {
    setShowBanner(false);
  };

  return { showBanner, dismissBanner, resetBaseVersion };
}